import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiAIFetch, apiFetch, getStoredUser, parseApiResponse } from '../api';
import { TranscriptCard, TranscriptDetailPanel, SyncStatusPanel, BrowseNotesTab, ManualTranscriptForm } from '../components/google-meet';
import { useSSEAnalysis } from '../hooks/useSSEAnalysis';
import useScopedStudents from '../hooks/useScopedStudents';
import '../styles/google-meet.css';

const GOOGLE_EMAIL_KEY = 'lrbrain-google-email';
const GOOGLE_MEET_DISMISSED_WARNING_KEY = 'lrbrain-google-meet-dismissed-warning-signature';
const GOOGLE_WORKSPACE_TABS = ['meet', 'calendar', 'gmail-chat'];
const GOOGLE_WORKSPACE_TAB_ENABLED = {
  meet: true,
  calendar: true,
  'gmail-chat': true
};
const GOOGLE_WORKSPACE_SESSION_KEY = 'lrbrain-google-workspace-tab';
const GOOGLE_MEET_TAB_SESSION_KEY = 'lrbrain-google-meet-active-tab';
const GOOGLE_MEET_TABS = ['synced', 'browse', 'manual', 'uploaded', 'analytics'];
const GOOGLE_AUTH_LAST_CHECKED_AT_KEY = 'lrbrain-google-auth-last-checked-at';
const GOOGLE_AUTH_CHECK_TTL_MS = 2 * 60 * 1000;
const CALENDAR_WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEADLINE_KEYWORDS = ['deadline', 'due', 'exam', 'midterm', 'final', 'proposal', 'submission', 'quiz', 'assignment'];
const GMAIL_NAME_FALLBACK = 'Unknown Sender';

const normalizeWorkspaceTab = (value) => {
  if (!value || !GOOGLE_WORKSPACE_TABS.includes(value)) return 'meet';
  return GOOGLE_WORKSPACE_TAB_ENABLED[value] ? value : 'meet';
};

const normalizeMeetTab = (value) => {
  if (!value) return 'synced';
  return GOOGLE_MEET_TABS.includes(value) ? value : 'synced';
};

const readSessionValue = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionValue = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage failures (private mode/security policies)
  }
};

const readSessionNumber = (key) => {
  const raw = readSessionValue(key);
  if (!raw) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const GoogleMeetSync = ({ studentMode = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceTab = studentMode ? 'meet' : normalizeWorkspaceTab(searchParams.get('workspace'));

  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [isRestoringAuth, setIsRestoringAuth] = useState(true);
  const [authResolved, setAuthResolved] = useState(false);
  const [searchScope, setSearchScope] = useState(null);
  const [permissions, setPermissions] = useState(null);

  // Transcripts state
  const [transcripts, setTranscripts] = useState([]);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [regeneratingSummaryId, setRegeneratingSummaryId] = useState(null);
  const [counts, setCounts] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  // Loading states
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Messages
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [syncResults, setSyncResults] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [dismissedWarningSignature, setDismissedWarningSignature] = useState('');
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(() => studentMode ? 'synced' : normalizeMeetTab(readSessionValue(GOOGLE_MEET_TAB_SESSION_KEY))); // 'synced' | 'browse' | 'manual' | 'uploaded' | 'analytics'
  const [isListCollapsed, setIsListCollapsed] = useState(false);

  // Analytics state
  const [analyticsTranscripts, setAnalyticsTranscripts] = useState([]);
  const [selectedAnalyticsTranscript, setSelectedAnalyticsTranscript] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [plannedSessions, setPlannedSessions] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [plannedSessionsLoading, setPlannedSessionsLoading] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState('compact');
  const [calendarMonthDate, setCalendarMonthDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [isCalendarDrawerOpen, setIsCalendarDrawerOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionEditForm, setSessionEditForm] = useState(null);
  const [gmailSending, setGmailSending] = useState(false);
  const [defaultSessionLink, setDefaultSessionLink] = useState('');
  const [sessionLinkDraft, setSessionLinkDraft] = useState('');
  const [isEditingSessionLink, setIsEditingSessionLink] = useState(false);
  const [sessionLinkSaving, setSessionLinkSaving] = useState(false);
  const [calendarForm, setCalendarForm] = useState({
    summary: '',
    description: '',
    start_time: '',
    end_time: '',
    attendees: ''
  });
  const [isAssignSessionDrawerOpen, setIsAssignSessionDrawerOpen] = useState(false);
  const [assignSessionForm, setAssignSessionForm] = useState({
    student_uuid: '',
    title: '',
    description: '',
    session_date: '',
    session_time: '15:00',
    duration_minutes: 60,
    participant_emails: '',
    participant_names: '',
    join_url: '',
    location: '',
    also_add_to_google_calendar: true,
    notify_student: true,
  });
  const [assignSessionLoading, setAssignSessionLoading] = useState(false);
  const [gmailForm, setGmailForm] = useState({
    to: '',
    subject: '',
    body_text: ''
  });
  const [gmailInbox, setGmailInbox] = useState([]);
  const [gmailInboxLoading, setGmailInboxLoading] = useState(false);
  const [gmailThreadLoading, setGmailThreadLoading] = useState(false);
  const [gmailSelectedThreadId, setGmailSelectedThreadId] = useState('');
  const [gmailSelectedThread, setGmailSelectedThread] = useState(null);
  const [gmailSearchInput, setGmailSearchInput] = useState('');
  const [gmailScopeReconnectNeeded, setGmailScopeReconnectNeeded] = useState(false);

  const [activeAnalysisTranscriptId, setActiveAnalysisTranscriptId] = useState(null);
  const [retryingSection, setRetryingSection] = useState(null);
  const autoSyncTriggeredRef = useRef(false);
  const hasBootstrappedAuthRef = useRef(false);
  const { students: scopedStudents, refetchStudents } = useScopedStudents({ skip: studentMode });

  const {
    startAnalysis,
    reset: resetAnalysis,
    progress: analysisProgress,
    summary: analysisSummary,
    error: analysisError,
    isAnalyzing
  } = useSSEAnalysis();

  const updateWorkspaceQuery = useCallback((workspace) => {
    if (studentMode) return;
    const normalizedWorkspace = normalizeWorkspaceTab(workspace);
    if (normalizeWorkspaceTab(searchParams.get('workspace')) === normalizedWorkspace) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('workspace', normalizedWorkspace);
    try {
      setSearchParams(next, { replace: true });
    } catch (err) {
      console.warn('Could not persist workspace tab in URL', err);
    }
  }, [searchParams, setSearchParams, studentMode]);

  const toUserMessage = useCallback((result, fallback) => {
    if (!result || !result.error) return fallback;
    const status = result.error.status;
    if (status === 401) return 'Your session expired. Please sign in again.';
    if (status === 403) return 'You do not have permission to perform this action.';
    if (status === 404) return result.error.message || 'Requested resource was not found.';
    if (status === 413) return 'Request is too large. Please reduce payload size.';
    return result.error.message || fallback;
  }, []);

  // Utility functions
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startStr, endStr) => {
    if (!startStr || !endStr) return 'N/A';
    const start = new Date(startStr);
    const end = new Date(endStr);
    const diffMs = end - start;
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const parseEventDate = useCallback((event, mode = 'start') => {
    const slot = mode === 'end' ? event?.end : event?.start;
    const raw = slot?.dateTime || slot?.date;
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const formatClockTime = useCallback((date) => {
    if (!date) return 'TBD';
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  }, []);

  const formatDateLabel = useCallback((date) => {
    if (!date) return 'Date TBD';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }, []);

  const formatDueLabel = useCallback((date) => {
    if (!date) return 'Date TBD';
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dueStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((dueStart.getTime() - todayStart.getTime()) / 86400000);
    if (diffDays <= 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  }, []);

  const splitList = useCallback((value) => {
    if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
    return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
  }, []);

  const formatDateTimeLocal = useCallback((date) => {
    if (!date || Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }, []);

  const getParticipantLabel = useCallback((session) => {
    const names = splitList(session?.participant_names);
    if (names.length) return names.join(', ');
    const emails = splitList(session?.participant_emails);
    if (emails.length) return emails.join(', ');
    return session?.student_email || '';
  }, [splitList]);

  const calendarEventsNormalized = useMemo(() => {
    return (calendarEvents || [])
      .map((event) => {
        const startDate = parseEventDate(event, 'start');
        const endDate = parseEventDate(event, 'end');
        if (!startDate) return null;
        return {
          ...event,
          __startDate: startDate,
          __endDate: endDate,
          __isDeadline: DEADLINE_KEYWORDS.some((keyword) => {
            const text = `${event?.summary || ''} ${event?.description || ''}`.toLowerCase();
            return text.includes(keyword);
          })
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.__startDate - b.__startDate);
  }, [calendarEvents, parseEventDate]);

  const plannedSessionsNormalized = useMemo(() => {
    return (plannedSessions || [])
      .map((session) => {
        const startDate = session.scheduled_at ? new Date(session.scheduled_at) : null;
        if (!startDate || Number.isNaN(startDate.getTime())) return null;
        const endDate = new Date(startDate.getTime() + (Number(session.duration_minutes) || 60) * 60000);
        return {
          ...session,
          __startDate: startDate,
          __endDate: endDate,
          __isPlannedSession: true,
          __participantLabel: getParticipantLabel(session)
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.__startDate - b.__startDate);
  }, [plannedSessions, getParticipantLabel]);

  const allCalendarItems = useMemo(
    () => [...calendarEventsNormalized, ...plannedSessionsNormalized]
      .sort((a, b) => a.__startDate - b.__startDate),
    [calendarEventsNormalized, plannedSessionsNormalized]
  );

  const upcomingSessions48h = useMemo(() => {
    const now = new Date();
    const horizon = now.getTime() + (48 * 60 * 60 * 1000);
    return allCalendarItems.filter((event) => {
      const t = event.__startDate.getTime();
      return t >= now.getTime() && t <= horizon;
    }).slice(0, 6);
  }, [allCalendarItems]);

  const deadlineEvents = useMemo(() => {
    return calendarEventsNormalized
      .filter((event) => event.__isDeadline)
      .slice(0, 5);
  }, [calendarEventsNormalized]);

  const calendarMonthCells = useMemo(() => {
    const year = calendarMonthDate.getFullYear();
    const month = calendarMonthDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay();
    const firstVisibleDay = new Date(year, month, 1 - startOffset);
    const cells = [];

    for (let i = 0; i < 42; i += 1) {
      const dayDate = new Date(firstVisibleDay);
      dayDate.setDate(firstVisibleDay.getDate() + i);
      const dayStart = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
      const dayEnd = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate(), 23, 59, 59, 999);
      const events = allCalendarItems.filter((event) => event.__startDate >= dayStart && event.__startDate <= dayEnd);

      cells.push({
        date: dayDate,
        isCurrentMonth: dayDate.getMonth() === month,
        isToday: dayDate.toDateString() === new Date().toDateString(),
        events
      });
    }

    return cells;
  }, [allCalendarItems, calendarMonthDate]);

  const normalizeSummary = (rawSummary) => {
    if (!rawSummary) return null;
    if (typeof rawSummary === 'object') return rawSummary;
    if (typeof rawSummary === 'string') {
      try {
        return JSON.parse(rawSummary);
      } catch {
        return { session_overview: rawSummary };
      }
    }
    return null;
  };

  const getSortTimestamp = useCallback((transcript) => {
    const candidates = [
      transcript.summary_generated_at,
      transcript.start_time,
      transcript.created_at,
      transcript.synced_at
    ];
    for (const candidate of candidates) {
      if (!candidate) continue;
      const parsed = new Date(candidate).getTime();
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 0;
  }, []);

  const buildAnalyticsTranscripts = useCallback((items) => {
    return [...(items || [])]
      .filter((t) => t.summary_source === 'AI_GENERATED')
      .sort((a, b) => getSortTimestamp(b) - getSortTimestamp(a));
  }, [getSortTimestamp]);

  const warningStorageKey = useCallback(() => {
    const accountKey = googleEmail || 'default';
    return `${GOOGLE_MEET_DISMISSED_WARNING_KEY}:${accountKey}`;
  }, [googleEmail]);

  const buildWarningSignature = useCallback((warningItems) => {
    if (!Array.isArray(warningItems) || warningItems.length === 0) {
      return '';
    }
    const normalized = warningItems
      .map((warning) => ({
        type: String(warning?.type || 'warning'),
        message: String(warning?.message || ''),
        count: Number.isFinite(warning?.count) ? warning.count : null
      }))
      .sort((a, b) => `${a.type}:${a.message}`.localeCompare(`${b.type}:${b.message}`));
    return JSON.stringify(normalized);
  }, []);

  // Fetch transcripts
  const fetchTranscripts = useCallback(async (email = null) => {
    setLoading(true);
    try {
      const query = email ? `?email=${encodeURIComponent(email)}` : '';
      const response = await apiFetch(`/api/google/meet/transcripts${query}`);
      const result = await parseApiResponse(response, 'google-meet-transcript-list');
      if (!result.ok) {
        throw new Error(result.error.message || 'Failed to fetch transcripts');
      }
      const data = result.data;
      const transcriptList = data.transcripts || [];
      if (data.transcripts) {
        setTranscripts(data.transcripts);
      }
      if (data.counts) {
        setCounts(data.counts);
      }
      if (data.last_sync_at) {
        setLastSyncAt(data.last_sync_at);
      }
      return transcriptList;
    } catch (err) {
      console.error('Error fetching transcripts:', err);
      setError('Failed to fetch transcripts');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Check auth status
  const checkAuthStatus = useCallback(async (email, options = {}) => {
    const { preserveConnectionOnError = false } = options;
    setLoading(true);
    try {
      const response = await apiFetch(`/api/google/auth/status?email=${encodeURIComponent(email)}`);
      const result = await parseApiResponse(response, 'google-auth-status');
      if (!result.ok) {
        setError(toUserMessage(result, 'Failed to check authentication status'));
        if (preserveConnectionOnError) {
          setIsConnected(true);
          setGoogleEmail((prev) => prev || email);
        }
        return 'error';
      }
      const data = result.data;
      writeSessionValue(GOOGLE_AUTH_LAST_CHECKED_AT_KEY, String(Date.now()));

      if (data.connected) {
        setIsConnected(true);
        setGoogleEmail(data.google_email);
        localStorage.setItem(GOOGLE_EMAIL_KEY, data.google_email);
        setSearchScope(data.search_scope);
        setPermissions(data.permissions);
        fetchTranscripts(data.google_email);
        return 'connected';
      } else {
        localStorage.removeItem(GOOGLE_EMAIL_KEY);
        setIsConnected(false);
        setGoogleEmail('');
        setSearchScope(null);
        setPermissions(null);
        return 'disconnected';
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      setError('Failed to check authentication status');
      if (preserveConnectionOnError) {
        setIsConnected(true);
        setGoogleEmail((prev) => prev || email);
      }
      return 'error';
    } finally {
      setLoading(false);
    }
  }, [fetchTranscripts, toUserMessage]);

  // Handle OAuth callback on mount
  useEffect(() => {
    if (hasBootstrappedAuthRef.current) return;
    hasBootstrappedAuthRef.current = true;

    const bootstrapAuth = async () => {
      setIsRestoringAuth(true);

      const status = searchParams.get('status');
      const email = searchParams.get('email');
      const message = searchParams.get('message');

      if (status === 'success' && email) {
        localStorage.setItem(GOOGLE_EMAIL_KEY, email);
        setGoogleEmail(email);
        setIsConnected(true);
        setSuccessMessage('Successfully connected your Google account!');
        const next = new URLSearchParams(searchParams);
        next.delete('status');
        next.delete('email');
        next.delete('message');
        if (!next.get('workspace')) {
          next.set('workspace', workspaceTab);
        }
        setSearchParams(next);
        await fetchTranscripts(email);
        setIsRestoringAuth(false);
        setAuthResolved(true);
        return;
      }

      if (status === 'error') {
        setError(message || 'Failed to connect Google account');
        const next = new URLSearchParams(searchParams);
        next.delete('status');
        next.delete('email');
        next.delete('message');
        if (!next.get('workspace')) {
          next.set('workspace', workspaceTab);
        }
        setSearchParams(next);
        setIsRestoringAuth(false);
        setAuthResolved(true);
        return;
      }

      const storedEmail = localStorage.getItem(GOOGLE_EMAIL_KEY);
      if (storedEmail) {
        setGoogleEmail(storedEmail);
        setIsConnected(true);

        const lastCheckedAt = readSessionNumber(GOOGLE_AUTH_LAST_CHECKED_AT_KEY);
        const shouldSkipStatusCheck = Date.now() - lastCheckedAt < GOOGLE_AUTH_CHECK_TTL_MS;

        if (shouldSkipStatusCheck) {
          await fetchTranscripts(storedEmail);
        } else {
          const statusResult = await checkAuthStatus(storedEmail, { preserveConnectionOnError: true });
          if (statusResult === 'error') {
            await fetchTranscripts(storedEmail);
          }
        }
      } else {
        const fallbackUserEmail = (getStoredUser()?.email || '').trim().toLowerCase();
        if (fallbackUserEmail) {
          await checkAuthStatus(fallbackUserEmail);
        }
      }

      setIsRestoringAuth(false);
      setAuthResolved(true);
    };

    bootstrapAuth().catch((err) => {
      console.error('Error during Google auth bootstrap:', err);
      setIsRestoringAuth(false);
      setAuthResolved(true);
    });
  }, [searchParams, setSearchParams, checkAuthStatus, fetchTranscripts, workspaceTab]);

  useEffect(() => {
    if (studentMode) return;
    if (searchParams.get('workspace')) return;
    const fromSession = readSessionValue(GOOGLE_WORKSPACE_SESSION_KEY);
    updateWorkspaceQuery(fromSession || 'meet');
  }, [searchParams, updateWorkspaceQuery, studentMode]);

  useEffect(() => {
    if (!authResolved) return;
    if (studentMode) return;
    writeSessionValue(GOOGLE_WORKSPACE_SESSION_KEY, workspaceTab);
  }, [workspaceTab, authResolved, studentMode]);

  useEffect(() => {
    if (!authResolved) return;
    writeSessionValue(GOOGLE_MEET_TAB_SESSION_KEY, normalizeMeetTab(activeTab));
  }, [activeTab, authResolved]);

  useEffect(() => {
    if (!authResolved) return;
    setActiveTab((prev) => {
      const normalized = normalizeMeetTab(prev);
      if (studentMode) return 'synced';
      if (isConnected) {
        return normalized === 'uploaded' ? 'synced' : normalized;
      }
      if (normalized === 'browse' || normalized === 'synced') return 'uploaded';
      return normalized;
    });
  }, [isConnected, authResolved, studentMode]);

  // Connect Google account
  const handleConnect = async () => {
    setLoading(true);
    setError(null);
    try {
      const returnPath = studentMode ? '/student/google-meet-sync' : '/teacher/google-meet-sync';
      const response = await apiFetch(`/api/google/auth/url?return_path=${encodeURIComponent(returnPath)}`);
      const result = await parseApiResponse(response, 'google-auth-url');
      if (!result.ok) {
        setError(toUserMessage(result, 'Failed to start Google authentication'));
        return;
      }
      const data = result.data;
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setError(data.error || 'Failed to get authorization URL');
      }
    } catch (err) {
      console.error('Error getting auth URL:', err);
      setError('Failed to start Google authentication');
    } finally {
      setLoading(false);
    }
  };

  // Disconnect Google account
  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Google account? All synced transcripts will be deleted.')) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/google/auth/disconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: googleEmail })
      });
      const result = await parseApiResponse(response, 'google-auth-disconnect');
      if (!result.ok) {
        setError(toUserMessage(result, 'Failed to disconnect'));
        return;
      }
      const data = result.data;
      if (data.success) {
        localStorage.removeItem(GOOGLE_EMAIL_KEY);
        setIsConnected(false);
        setGoogleEmail('');
        setTranscripts([]);
        setSelectedTranscript(null);
        setAnalyticsTranscripts([]);
        setSelectedAnalyticsTranscript(null);
        setSuccessMessage('Successfully disconnected');
      } else {
        setError(data.error || 'Failed to disconnect');
      }
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Failed to disconnect Google account');
    } finally {
      setLoading(false);
    }
  };

  // Sync transcripts
  const handleSync = useCallback(async (source = 'manual') => {
    const isAuto = source === 'auto';
    if (syncing) return;

    setSyncing(true);
    if (isAuto) {
      setAutoSyncStatus({ type: 'syncing', message: 'Auto-syncing in background...' });
    } else {
      setAutoSyncStatus(null);
      setError(null);
      setSyncResults(null);
      setWarnings([]);
    }

    try {
      const response = await apiFetch('/api/google/meet/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: googleEmail, max_meetings: 10 })
      });
      const result = await parseApiResponse(response, 'google-meet-sync');
      if (!result.ok) {
        const message = toUserMessage(result, 'Failed to sync Google Meet transcripts');
        if (isAuto) {
          setAutoSyncStatus({ type: 'error', message: 'Auto-sync failed. You can use Sync Now.' });
          console.warn('Background auto-sync failed:', message);
        } else {
          setError(message);
        }
        return;
      }
      const data = result.data;
      if (data.success) {
        setSyncResults(data);
        setLastSyncAt(data.synced_at);
        if (data.warnings && data.warnings.length > 0) {
          setWarnings(data.warnings);
        }
        if (!isAuto) {
          setSuccessMessage(`Synced ${data.transcripts_synced} transcript(s) from ${data.meetings_found} meeting(s)`);
        } else {
          setAutoSyncStatus(null);
        }
        fetchTranscripts(googleEmail);
      } else {
        const message = data.error || 'Sync failed';
        if (isAuto) {
          setAutoSyncStatus({ type: 'error', message: 'Auto-sync failed. You can use Sync Now.' });
          console.warn('Background auto-sync failed:', message);
        } else {
          setError(message);
        }
      }
    } catch (err) {
      console.error('Error syncing:', err);
      if (isAuto) {
        setAutoSyncStatus({ type: 'error', message: 'Auto-sync failed. You can use Sync Now.' });
      } else {
        setError('Failed to sync Google Meet transcripts');
      }
    } finally {
      setSyncing(false);
    }
  }, [fetchTranscripts, googleEmail, syncing, toUserMessage]);

  // Select transcript
  const loadTranscriptDetail = useCallback(async (transcriptId) => {
    setLoading(true);
    try {
      const response = await apiFetch(`/api/google/meet/transcripts/${transcriptId}`);
      const result = await parseApiResponse(response, 'google-meet-transcript-detail');
      const data = result.data;
      if (!result.ok) {
        setError(result.error.message || 'Failed to load transcript');
        return null;
      }
      if (data.id) {
        setSelectedTranscript(data);
        return data;
      } else {
        setError(data.error || 'Failed to load transcript');
        return null;
      }
    } catch (err) {
      console.error('Error loading transcript:', err);
      setError('Failed to load transcript');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAnalyticsTranscriptDetail = useCallback(async (transcriptId) => {
    if (!transcriptId) return null;
    setAnalyticsLoading(true);
    try {
      const response = await apiFetch(`/api/google/meet/transcripts/${transcriptId}`);
      const result = await parseApiResponse(response, 'google-meet-transcript-analytics-detail');
      const data = result.data;
      if (!result.ok) {
        setError(result.error.message || 'Failed to load analyzed transcript');
        return null;
      }
      if (!data?.id) {
        setError('Failed to load analyzed transcript');
        return null;
      }
      setSelectedAnalyticsTranscript(data);
      return data;
    } catch (err) {
      console.error('Error loading analyzed transcript:', err);
      setError('Failed to load analyzed transcript');
      return null;
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const loadAnalyticsTabData = useCallback(async () => {
    const list = await fetchTranscripts(isConnected ? googleEmail : null);
    const filtered = buildAnalyticsTranscripts(list);
    setAnalyticsTranscripts(filtered);

    if (filtered.length === 0) {
      setSelectedAnalyticsTranscript(null);
      return;
    }

    const activeSelectionId = selectedAnalyticsTranscript?.id;
    const existing = filtered.find((t) => t.id === activeSelectionId);
    const preferred = existing || filtered[0];
    if (!activeSelectionId || activeSelectionId !== preferred.id) {
      await loadAnalyticsTranscriptDetail(preferred.id);
    }
  }, [
    buildAnalyticsTranscripts,
    fetchTranscripts,
    googleEmail,
    isConnected,
    loadAnalyticsTranscriptDetail,
    selectedAnalyticsTranscript?.id
  ]);

  // Select transcript
  const handleSelectTranscript = async (transcript) => {
    if (selectedTranscript?.id === transcript.id) return;
    await loadTranscriptDetail(transcript.id);
  };

  const fetchCalendarEvents = useCallback(async () => {
    if (!googleEmail) return;
    setCalendarLoading(true);
    try {
      const response = await apiFetch(`/api/google/calendar/events?email=${encodeURIComponent(googleEmail)}&max_results=20&days_ahead=14`);
      const result = await parseApiResponse(response, 'google-calendar-events');
      if (!result.ok) {
        setError(toUserMessage(result, 'Failed to fetch calendar events'));
        return;
      }
      setCalendarEvents(result.data?.events || []);
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      setError('Failed to fetch calendar events');
    } finally {
      setCalendarLoading(false);
    }
  }, [googleEmail, toUserMessage]);

  const fetchPlannedSessions = useCallback(async () => {
    setPlannedSessionsLoading(true);
    try {
      const response = await apiFetch('/api/planned-sessions');
      const result = await parseApiResponse(response, 'planned-session-list');
      if (!result.ok) {
        setError(toUserMessage(result, 'Failed to fetch planned sessions'));
        return;
      }
      setPlannedSessions(result.data?.sessions || []);
    } catch (err) {
      console.error('Error fetching planned sessions:', err);
      setError('Failed to fetch planned sessions');
    } finally {
      setPlannedSessionsLoading(false);
    }
  }, [toUserMessage]);

  const fetchDefaultSessionLink = useCallback(async () => {
    try {
      const query = googleEmail ? `?google_email=${encodeURIComponent(googleEmail)}` : '';
      const response = await apiFetch(`/api/tutor/session-link${query}`);
      const result = await parseApiResponse(response, 'tutor-session-link');
      if (!result.ok) return;
      const joinUrl = result.data?.join_url || '';
      setDefaultSessionLink(joinUrl);
      setSessionLinkDraft(joinUrl);
      setAssignSessionForm((prev) => prev.join_url ? prev : { ...prev, join_url: joinUrl });
    } catch (err) {
      console.error('Error fetching default session link:', err);
    }
  }, [googleEmail]);

  const handleSaveDefaultSessionLink = async () => {
    setSessionLinkSaving(true);
    setError(null);
    try {
      const response = await apiFetch('/api/tutor/session-link', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          join_url: sessionLinkDraft.trim(),
          google_email: googleEmail || undefined,
        })
      });
      const result = await parseApiResponse(response, 'tutor-session-link-update');
      if (!result.ok) {
        setError(toUserMessage(result, 'Failed to save session link'));
        return;
      }
      const joinUrl = result.data?.join_url || '';
      setDefaultSessionLink(joinUrl);
      setSessionLinkDraft(joinUrl);
      setAssignSessionForm((prev) => prev.join_url ? prev : { ...prev, join_url: joinUrl });
      setIsEditingSessionLink(false);
      setSuccessMessage('Session launch link saved.');
    } catch (err) {
      console.error('Error saving session link:', err);
      setError('Failed to save session link');
    } finally {
      setSessionLinkSaving(false);
    }
  };

  const handleCreateCalendarEvent = async () => {
    if (!googleEmail) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/google/calendar/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: googleEmail,
          ...calendarForm,
          attendees: calendarForm.attendees
        })
      });
      const result = await parseApiResponse(response, 'google-calendar-create-event');
      if (!result.ok) {
        setError(toUserMessage(result, 'Failed to create calendar event'));
        return;
      }
      setSuccessMessage('Calendar event created.');
      setCalendarForm((prev) => ({ ...prev, summary: '', description: '', attendees: '' }));
      setIsCalendarDrawerOpen(false);
      fetchCalendarEvents();
    } catch (err) {
      console.error('Error creating calendar event:', err);
      setError('Failed to create calendar event');
    } finally {
      setLoading(false);
    }
  };

  const [assignSessionError, setAssignSessionError] = useState(null);

  const handleAssignSession = async () => {
    setAssignSessionError(null);

    // Resolve student
    const selectedStudent = scopedStudents.find((s) => s.uuid === assignSessionForm.student_uuid);
    const studentEmail = selectedStudent?.owner || '';
    if (!studentEmail || studentEmail === 'admin@lrbrain.com' || studentEmail === 'demo-admin@lrbrain.com' || studentEmail === 'demo-student') {
      setAssignSessionError('This student has no login email on file. Open the student profile panel, edit the student, and set a Student Login Email before assigning a session.');
      return;
    }

    // Build scheduled_at from separate date + time fields
    if (!assignSessionForm.session_date || !assignSessionForm.session_time) {
      setAssignSessionError('Please select both a date and a time for the session.');
      return;
    }
    const scheduledAt = `${assignSessionForm.session_date}T${assignSessionForm.session_time}`;
    const participantEmails = splitList(assignSessionForm.participant_emails);
    if (studentEmail && !participantEmails.includes(studentEmail)) {
      participantEmails.unshift(studentEmail);
    }
    const participantNames = splitList(assignSessionForm.participant_names);
    const displayName = selectedStudent?.display_name || `${selectedStudent?.first_name || ''} ${selectedStudent?.last_name || ''}`.trim();
    if (displayName && participantNames.length === 0) {
      participantNames.push(displayName);
    }

    setAssignSessionLoading(true);
    try {
      let googleCalendarEventId = null;
      if (assignSessionForm.also_add_to_google_calendar && isConnected && googleEmail) {
        const startDt = new Date(scheduledAt);
        const endDt = new Date(startDt.getTime() + Number(assignSessionForm.duration_minutes) * 60000);
        const calendarResponse = await apiFetch('/api/google/calendar/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: googleEmail,
            summary: assignSessionForm.title,
            description: [
              assignSessionForm.description,
              assignSessionForm.join_url ? `Join: ${assignSessionForm.join_url}` : ''
            ].filter(Boolean).join('\n\n'),
            start_time: scheduledAt,
            end_time: endDt.toISOString().slice(0, 16),
            attendees: participantEmails,
            location: assignSessionForm.location || assignSessionForm.join_url,
          }),
        });
        const calendarResult = await parseApiResponse(calendarResponse, 'google-calendar-create-event');
        if (calendarResult.ok) {
          googleCalendarEventId = calendarResult.data?.event?.id || null;
          fetchCalendarEvents();
        }
      }

      const response = await apiFetch('/api/planned-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_email: studentEmail,
          title: assignSessionForm.title,
          description: assignSessionForm.description,
          scheduled_at: scheduledAt,
          duration_minutes: Number(assignSessionForm.duration_minutes),
          participant_emails: participantEmails,
          participant_names: participantNames,
          join_url: assignSessionForm.join_url,
          location: assignSessionForm.location,
          google_calendar_event_id: googleCalendarEventId,
          notify_student: assignSessionForm.notify_student,
        }),
      });
      const result = await parseApiResponse(response, 'assign-planned-session');
      if (!result.ok) {
        setAssignSessionError(toUserMessage(result, 'Failed to assign session'));
        return;
      }

      setSuccessMessage('Session assigned successfully.');
      setAssignSessionForm({
        student_uuid: '',
        title: '',
        description: '',
        session_date: '',
        session_time: '15:00',
        duration_minutes: 60,
        participant_emails: '',
        participant_names: '',
        join_url: defaultSessionLink,
        location: '',
        also_add_to_google_calendar: true,
        notify_student: true,
      });
      setIsAssignSessionDrawerOpen(false);
      fetchPlannedSessions();
    } catch (err) {
      console.error('Error assigning session:', err);
      setAssignSessionError('Failed to assign session. Please try again.');
    } finally {
      setAssignSessionLoading(false);
    }
  };

  const openEditSession = (session) => {
    const start = session.__startDate || new Date(session.scheduled_at);
    setEditingSession(session);
    setSessionEditForm({
      title: session.title || '',
      description: session.description || '',
      scheduled_at: formatDateTimeLocal(start),
      duration_minutes: session.duration_minutes || 60,
      participant_emails: splitList(session.participant_emails).join(', '),
      participant_names: splitList(session.participant_names).join(', '),
      join_url: session.join_url || '',
      location: session.location || '',
      status: session.status || 'scheduled',
      google_calendar_event_id: session.google_calendar_event_id || '',
    });
  };

  const handleUpdatePlannedSession = async () => {
    if (!editingSession || !sessionEditForm) return;
    setAssignSessionLoading(true);
    setError(null);
    const startDate = new Date(sessionEditForm.scheduled_at);
    const endDate = new Date(startDate.getTime() + Number(sessionEditForm.duration_minutes) * 60000);
    const participantEmails = splitList(sessionEditForm.participant_emails);
    try {
      const response = await apiFetch(`/api/planned-sessions/${editingSession.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: sessionEditForm.title,
          description: sessionEditForm.description,
          scheduled_at: sessionEditForm.scheduled_at,
          duration_minutes: Number(sessionEditForm.duration_minutes),
          participant_emails: participantEmails,
          participant_names: splitList(sessionEditForm.participant_names),
          join_url: sessionEditForm.join_url,
          location: sessionEditForm.location,
          status: sessionEditForm.status,
          google_calendar_event_id: sessionEditForm.google_calendar_event_id,
        })
      });
      const result = await parseApiResponse(response, 'planned-session-update');
      if (!result.ok) {
        setError(toUserMessage(result, 'Failed to update session'));
        return;
      }

      if (sessionEditForm.google_calendar_event_id && isConnected && googleEmail) {
        const calendarResponse = await apiFetch(`/api/google/calendar/events/${encodeURIComponent(sessionEditForm.google_calendar_event_id)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: googleEmail,
            summary: sessionEditForm.title,
            description: [
              sessionEditForm.description,
              sessionEditForm.join_url ? `Join: ${sessionEditForm.join_url}` : ''
            ].filter(Boolean).join('\n\n'),
            start_time: sessionEditForm.scheduled_at,
            end_time: formatDateTimeLocal(endDate),
            attendees: participantEmails,
            location: sessionEditForm.location || sessionEditForm.join_url,
          })
        });
        const calendarResult = await parseApiResponse(calendarResponse, 'google-calendar-update-event');
        if (!calendarResult.ok) {
          setError('Session saved, but Google Calendar could not be updated.');
        } else {
          fetchCalendarEvents();
        }
      }

      setSuccessMessage('Session updated.');
      setEditingSession(null);
      setSessionEditForm(null);
      fetchPlannedSessions();
    } catch (err) {
      console.error('Error updating session:', err);
      setError('Failed to update session');
    } finally {
      setAssignSessionLoading(false);
    }
  };

  const handleGmailAction = async (mode = 'send') => {
    if (!googleEmail) return;
    setGmailSending(true);
    setError(null);
    try {
      const endpoint = mode === 'draft' ? '/api/google/gmail/draft' : '/api/google/gmail/send';
      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: googleEmail,
          to: gmailForm.to,
          subject: gmailForm.subject,
          body_text: gmailForm.body_text,
        })
      });
      const result = await parseApiResponse(response, `google-gmail-${mode}`);
      if (!result.ok) {
        setError(toUserMessage(result, `Failed to ${mode === 'draft' ? 'create draft' : 'send email'}`));
        return;
      }
      setSuccessMessage(mode === 'draft' ? 'Draft created in Gmail.' : 'Email sent.');
      if (mode === 'send') {
        setGmailForm({ to: '', subject: '', body_text: '' });
      }
    } catch (err) {
      console.error(`Error during Gmail ${mode}:`, err);
      setError(`Failed to ${mode === 'draft' ? 'create draft' : 'send email'}`);
    } finally {
      setGmailSending(false);
    }
  };

  const parseEmailAddress = useCallback((value) => {
    if (!value) return '';
    const match = String(value).match(/<([^>]+)>/);
    if (match && match[1]) return match[1].trim();
    const trimmed = String(value).trim();
    if (trimmed.includes('@') && !trimmed.includes(' ')) return trimmed;
    return '';
  }, []);

  const formatThreadTimestamp = useCallback((value) => {
    if (!value) return '';
    const asNumber = Number(value);
    const date = Number.isFinite(asNumber) && asNumber > 0 ? new Date(asNumber) : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }, []);

  const isGmailScopeError = useCallback((result) => {
    const errorCode = result?.data?.error_code || result?.error?.code;
    const message = `${result?.error?.message || ''} ${result?.data?.error || ''}`.toLowerCase();
    return result?.status === 403 && (
      errorCode === 'google_insufficient_scope'
      || message.includes('insufficient authentication scopes')
      || message.includes('insufficient permission')
    );
  }, []);

  const fetchGmailInbox = useCallback(async (query = '') => {
    if (!googleEmail) return;
    setGmailInboxLoading(true);
    try {
      const queryPart = query ? `&q=${encodeURIComponent(query)}` : '';
      const response = await apiFetch(`/api/google/gmail/inbox?email=${encodeURIComponent(googleEmail)}&max_results=25${queryPart}`);
      const result = await parseApiResponse(response, 'google-gmail-inbox');
      if (!result.ok) {
        if (isGmailScopeError(result)) {
          setGmailScopeReconnectNeeded(true);
          setError(null);
          setGmailInbox([]);
          setGmailSelectedThread(null);
          setGmailSelectedThreadId('');
          return;
        }
        setGmailScopeReconnectNeeded(false);
        setError(toUserMessage(result, 'Failed to load inbox'));
        return;
      }
      setGmailScopeReconnectNeeded(false);
      const items = result.data?.messages || [];
      setGmailInbox(items);
      if (items.length > 0) {
        const nextThreadId = items[0]?.thread_id || '';
        setGmailSelectedThreadId((prev) => prev || nextThreadId);
      } else {
        setGmailSelectedThreadId('');
        setGmailSelectedThread(null);
      }
    } catch (err) {
      console.error('Error loading Gmail inbox:', err);
      setError('Failed to load inbox');
    } finally {
      setGmailInboxLoading(false);
    }
  }, [googleEmail, isGmailScopeError, toUserMessage]);

  const fetchGmailThread = useCallback(async (threadId) => {
    if (!googleEmail || !threadId) return;
    setGmailThreadLoading(true);
    try {
      const response = await apiFetch(`/api/google/gmail/threads/${encodeURIComponent(threadId)}?email=${encodeURIComponent(googleEmail)}`);
      const result = await parseApiResponse(response, 'google-gmail-thread');
      if (!result.ok) {
        if (isGmailScopeError(result)) {
          setGmailScopeReconnectNeeded(true);
          setError(null);
          setGmailSelectedThread(null);
          return;
        }
        setGmailScopeReconnectNeeded(false);
        setError(toUserMessage(result, 'Failed to load conversation thread'));
        return;
      }
      setGmailScopeReconnectNeeded(false);
      const thread = result.data?.thread || null;
      setGmailSelectedThread(thread);
      const firstInbound = (thread?.messages || []).find((msg) => !msg?.is_from_me);
      if (firstInbound) {
        const toEmail = parseEmailAddress(firstInbound.from) || firstInbound.from_email || '';
        setGmailForm((prev) => ({
          ...prev,
          to: toEmail || prev.to,
          subject: prev.subject || (thread?.subject ? `Re: ${thread.subject}` : prev.subject),
        }));
      }
    } catch (err) {
      console.error('Error loading Gmail thread:', err);
      setError('Failed to load conversation thread');
    } finally {
      setGmailThreadLoading(false);
    }
  }, [googleEmail, isGmailScopeError, parseEmailAddress, toUserMessage]);

  const handleSelectAnalyticsTranscript = async (transcript) => {
    if (selectedAnalyticsTranscript?.id === transcript.id) return;
    await loadAnalyticsTranscriptDetail(transcript.id);
  };

  // Handle student link update
  const handleStudentLinked = (transcriptId, studentUuid) => {
    setTranscripts(prev =>
      prev.map(t =>
        t.id === transcriptId
          ? { ...t, linked_student_uuid: studentUuid }
          : t
      )
    );
    refetchStudents();
  };

  const handleSummaryUpdated = (transcriptId, summary, source, generatedAt) => {
    const analysisStatus = typeof summary === 'object' && summary
      ? summary.analysis_status || null
      : null;
    setTranscripts(prev =>
      prev.map(t =>
        t.id === transcriptId
          ? { ...t, summary, summary_source: source, summary_generated_at: generatedAt, analysis_status: analysisStatus }
          : t
      )
    );
    setSelectedTranscript(prev =>
      prev && prev.id === transcriptId
        ? { ...prev, summary, summary_source: source, summary_generated_at: generatedAt, analysis_status: analysisStatus }
        : prev
    );
    setSelectedAnalyticsTranscript(prev =>
      prev && prev.id === transcriptId
        ? { ...prev, summary, summary_source: source, summary_generated_at: generatedAt, analysis_status: analysisStatus }
        : prev
    );
  };

  const handleRegenerateSummary = async (transcriptId) => {
    if (!transcriptId) return;
    setRegeneratingSummaryId(transcriptId);
    setError(null);
    try {
      const response = await apiFetch(`/api/google/meet/transcripts/${transcriptId}/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force_regenerate: true })
      });
      const result = await parseApiResponse(response, 'google-meet-summary-regenerate');
      if (!result.ok) {
        throw new Error(result.error.message || 'Failed to regenerate summary');
      }
      const data = result.data;

      if (data?.summary) {
        handleSummaryUpdated(transcriptId, data.summary, data.source, data.generated_at);
        setSuccessMessage('Summary regenerated');
      } else {
        throw new Error('Failed to regenerate summary');
      }
    } catch (err) {
      console.error('Error regenerating summary:', err);
      setError(err.message || 'Failed to regenerate summary');
    } finally {
      setRegeneratingSummaryId(null);
    }
  };

  const runAiSummary = useCallback(async (transcriptId) => {
    if (!transcriptId) return;
    setActiveAnalysisTranscriptId(transcriptId);
    setError(null);
    setSuccessMessage('Analyzing transcript with AI...');
    resetAnalysis();
    await startAnalysis(transcriptId, 'google_meet.analyze_stream');
  }, [resetAnalysis, startAnalysis]);

  const retryAiSummarySection = useCallback(async (transcriptId, sectionKey) => {
    if (!transcriptId || !sectionKey) return;
    setRetryingSection({ transcriptId, sectionKey });
    setError(null);
    try {
      const response = await apiAIFetch(
        `/api/google/meet/transcripts/${transcriptId}/analyze-section`,
        'google_meet.analyze_section',
        {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_key: sectionKey })
        }
      );
      const result = await parseApiResponse(response, 'google-meet-analyze-section');
      if (!result.ok) {
        throw new Error(result.error.message || 'Failed to retry section analysis');
      }
      const data = result.data || {};
      handleSummaryUpdated(
        transcriptId,
        data.summary,
        data.source || 'AI_GENERATED',
        data.generated_at || new Date().toISOString()
      );
      setSuccessMessage(data.section?.status === 'complete'
        ? `Section "${sectionKey}" completed.`
        : `Section "${sectionKey}" updated.`);
    } catch (err) {
      console.error('Error retrying section:', err);
      setError(err.message || 'Failed to retry section');
    } finally {
      setRetryingSection(null);
    }
  }, []);

  const jumpToUploadedTranscript = useCallback(async (transcript, summarize = false) => {
    if (!transcript?.id) return;

    const targetTab = isConnected ? 'synced' : 'uploaded';
    setActiveTab(targetTab);

    const list = await fetchTranscripts(isConnected ? googleEmail : null);
    const matched = list.find((t) => t.id === transcript.id) || transcript;
    await loadTranscriptDetail(matched.id);

    if (summarize) {
      await runAiSummary(matched.id);
    }
  }, [fetchTranscripts, googleEmail, isConnected, loadTranscriptDetail, runAiSummary]);

  const openFullTranscriptFromAnalytics = async () => {
    if (!selectedAnalyticsTranscript?.id) return;

    setSelectedTranscript(selectedAnalyticsTranscript);
    setActiveTab(isConnected ? 'synced' : 'uploaded');
    await loadTranscriptDetail(selectedAnalyticsTranscript.id);
  };

  // Clear messages after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (analysisSummary && activeAnalysisTranscriptId && isAnalyzing) {
      handleSummaryUpdated(
        activeAnalysisTranscriptId,
        analysisSummary,
        'AI_GENERATED',
        new Date().toISOString()
      );
    }
  }, [analysisSummary, activeAnalysisTranscriptId, isAnalyzing]);

  useEffect(() => {
    if (analysisSummary && activeAnalysisTranscriptId && !isAnalyzing) {
      const generatedAt = new Date().toISOString();
      handleSummaryUpdated(activeAnalysisTranscriptId, analysisSummary, 'AI_GENERATED', generatedAt);
      if (analysisSummary.analysis_status === 'COMPLETE') {
        setSuccessMessage('AI summary completed.');
      } else {
        setSuccessMessage('AI summary partially completed. Retry failed sections to finish.');
      }
      setActiveAnalysisTranscriptId(null);
    }
  }, [analysisSummary, activeAnalysisTranscriptId, isAnalyzing]);

  useEffect(() => {
    if (analysisError) {
      setError(analysisError);
      setActiveAnalysisTranscriptId(null);
    }
  }, [analysisError]);

  useEffect(() => {
    try {
      const storedSignature = localStorage.getItem(warningStorageKey()) || '';
      setDismissedWarningSignature(storedSignature);
    } catch (storageError) {
      console.warn('Could not read dismissed warning state:', storageError);
      setDismissedWarningSignature('');
    }
  }, [warningStorageKey]);

  // Refresh analytics data on analytics tab entry
  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalyticsTabData();
    }
  }, [activeTab, loadAnalyticsTabData]);

  // Keep analytics list synchronized with transcript updates
  useEffect(() => {
    const filtered = buildAnalyticsTranscripts(transcripts);
    setAnalyticsTranscripts(filtered);

    if (filtered.length === 0) {
      setSelectedAnalyticsTranscript(null);
      return;
    }

    if (!selectedAnalyticsTranscript?.id) {
      return;
    }

    const exists = filtered.find((t) => t.id === selectedAnalyticsTranscript.id);
    if (!exists) {
      setSelectedAnalyticsTranscript(null);
    }
  }, [transcripts, buildAnalyticsTranscripts, selectedAnalyticsTranscript?.id]);

  useEffect(() => {
    setIsListCollapsed(false);
  }, [activeTab]);

  useEffect(() => {
    if (!isConnected || activeTab !== 'synced' || !googleEmail) return;
    if (autoSyncTriggeredRef.current) return;
    if (syncing) return;

    autoSyncTriggeredRef.current = true;
    handleSync('auto');
  }, [activeTab, googleEmail, handleSync, isConnected, syncing]);

  useEffect(() => {
    if (!isConnected || !googleEmail) return;
    if (workspaceTab === 'calendar') {
      fetchCalendarEvents();
    }
  }, [workspaceTab, isConnected, googleEmail, fetchCalendarEvents]);

  useEffect(() => {
    if (workspaceTab !== 'calendar') return;
    fetchPlannedSessions();
  }, [workspaceTab, fetchPlannedSessions]);

  useEffect(() => {
    if (!isConnected || !googleEmail) return;
    fetchDefaultSessionLink();
  }, [isConnected, googleEmail, fetchDefaultSessionLink]);

  useEffect(() => {
    if (!isConnected || !googleEmail) return;
    if (workspaceTab !== 'gmail-chat') return;
    fetchGmailInbox('');
  }, [workspaceTab, isConnected, googleEmail, fetchGmailInbox]);

  useEffect(() => {
    if (workspaceTab !== 'gmail-chat') return;
    if (!gmailSelectedThreadId) return;
    fetchGmailThread(gmailSelectedThreadId);
  }, [workspaceTab, gmailSelectedThreadId, fetchGmailThread]);

  useEffect(() => {
    if (workspaceTab !== 'gmail-chat') return;
    if (!gmailInbox.length) {
      setGmailSelectedThreadId('');
      setGmailSelectedThread(null);
      return;
    }
    const exists = gmailInbox.some((item) => item.thread_id === gmailSelectedThreadId);
    if (!exists) {
      setGmailSelectedThreadId(gmailInbox[0].thread_id || '');
    }
  }, [workspaceTab, gmailInbox, gmailSelectedThreadId]);

  useEffect(() => {
    if (workspaceTab !== 'gmail-chat') return;
    const timer = setTimeout(() => {
      fetchGmailInbox(gmailSearchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [workspaceTab, gmailSearchInput, fetchGmailInbox]);

  useEffect(() => {
    if (workspaceTab !== 'calendar' && isCalendarDrawerOpen) {
      setIsCalendarDrawerOpen(false);
    }
  }, [workspaceTab, isCalendarDrawerOpen]);

  useEffect(() => {
    if (workspaceTab !== 'gmail-chat' && gmailScopeReconnectNeeded) {
      setGmailScopeReconnectNeeded(false);
    }
  }, [workspaceTab, gmailScopeReconnectNeeded]);

  // Handler for when a manual transcript is created
  const handleManualTranscriptCreated = (transcript) => {
    // Always refresh transcript list after manual create.
    fetchTranscripts(isConnected ? googleEmail : null);
    setSuccessMessage(`Transcript "${transcript.title}" created! Go to Synced Sessions to view and generate a summary.`);
  };

  const summaryStats = [
    { key: 'total', label: 'Total Meetings', value: counts?.total || 0, icon: 'meetings', tone: 'blue' },
    { key: 'transcripts', label: 'Transcripts Generated', value: counts?.with_transcript || 0, icon: 'transcripts', tone: 'blue' },
    { key: 'notes', label: 'Sessions With Notes', value: counts?.with_notes || 0, icon: 'notes', tone: 'blue' },
    { key: 'pending', label: 'Pending Review', value: counts?.pending || 0, icon: 'pending', tone: 'coral' }
  ];

  const warningSignature = buildWarningSignature(warnings);
  const visibleWarnings = warningSignature !== '' && warningSignature === dismissedWarningSignature
    ? []
    : warnings;

  const handleDismissWarnings = useCallback(() => {
    if (!warningSignature) return;
    setDismissedWarningSignature(warningSignature);
    try {
      localStorage.setItem(warningStorageKey(), warningSignature);
    } catch (storageError) {
      console.warn('Could not persist dismissed warning state:', storageError);
    }
  }, [warningSignature, warningStorageKey]);

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const renderStatIcon = (icon) => {
    switch (icon) {
      case 'meetings':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="6" width="13" height="12" rx="2" />
            <path d="M16 10l5-3v10l-5-3z" />
          </svg>
        );
      case 'transcripts':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M8 13h8M8 17h5" />
          </svg>
        );
      case 'notes':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M16 3h5v5" />
            <path d="M20 14v7H4V3h7" />
            <path d="M14 3l7 7" />
            <path d="M8 12h8M8 16h6" />
          </svg>
        );
      case 'pending':
        return (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 8v5" />
            <circle cx="12" cy="16" r="1" />
            <path d="M21 16V8a2 2 0 0 0-2-2h-3V3H8v3H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const renderMainTabs = (mode) => {
    if (studentMode) {
      return (
        <div className="main-tab-navigation" role="tablist">
          <button
            className="main-tab-btn active"
            onClick={() => setActiveTab('synced')}
            role="tab"
            aria-selected="true"
          >
            {mode === 'connected' ? 'Synced Sessions' : 'Connect Google'}
          </button>
        </div>
      );
    }

    if (mode === 'connected') {
      return (
        <div className="main-tab-navigation" role="tablist">
          <button
            className={`main-tab-btn ${activeTab === 'synced' ? 'active' : ''}`}
            onClick={() => setActiveTab('synced')}
            role="tab"
            aria-selected={activeTab === 'synced'}
          >
            Synced Sessions
          </button>
          <button
            className={`main-tab-btn ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
            role="tab"
            aria-selected={activeTab === 'browse'}
          >
            Browse All Notes
          </button>
          <button
            className={`main-tab-btn ${activeTab === 'manual' ? 'active' : ''}`}
            onClick={() => setActiveTab('manual')}
            role="tab"
            aria-selected={activeTab === 'manual'}
          >
            Manual Entry
          </button>
          <button
            className={`main-tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
            role="tab"
            aria-selected={activeTab === 'analytics'}
          >
            Transcript Analytics
          </button>
        </div>
      );
    }

    return (
      <div className="main-tab-navigation" role="tablist">
        <button
          className={`main-tab-btn ${activeTab === 'synced' ? 'active' : ''}`}
          onClick={() => setActiveTab('synced')}
          role="tab"
          aria-selected={activeTab === 'synced'}
        >
          Connect Google
        </button>
        <button
          className={`main-tab-btn ${activeTab === 'manual' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('manual');
            refetchStudents();
          }}
          role="tab"
          aria-selected={activeTab === 'manual'}
        >
          Manual Entry
        </button>
        <button
          className={`main-tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('analytics');
            fetchTranscripts();
          }}
          role="tab"
          aria-selected={activeTab === 'analytics'}
        >
          Transcript Analytics
        </button>
        <button
          className={`main-tab-btn ${activeTab === 'uploaded' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('uploaded');
            fetchTranscripts();
          }}
          role="tab"
          aria-selected={activeTab === 'uploaded'}
        >
          Uploaded Transcripts
        </button>
      </div>
    );
  };

  const renderWorkspaceTabs = () => studentMode ? (
    <div className="google-workspace-tabs" role="tablist" aria-label="Google workspace sections">
      <button
        className="google-workspace-tab active"
        type="button"
        role="tab"
        aria-selected="true"
      >
        Meet
      </button>
    </div>
  ) : (
    <div className="google-workspace-tabs" role="tablist" aria-label="Google workspace sections">
      <button
        className={`google-workspace-tab ${workspaceTab === 'meet' ? 'active' : ''}`}
        onClick={() => updateWorkspaceQuery('meet')}
        role="tab"
        aria-selected={workspaceTab === 'meet'}
      >
        Meet
      </button>
      <button
        className={`google-workspace-tab ${workspaceTab === 'calendar' ? 'active' : ''}`}
        onClick={() => updateWorkspaceQuery('calendar')}
        disabled={!GOOGLE_WORKSPACE_TAB_ENABLED.calendar}
        role="tab"
        aria-disabled={!GOOGLE_WORKSPACE_TAB_ENABLED.calendar}
        aria-selected={workspaceTab === 'calendar'}
      >
        Calendar
      </button>
      <button
        className={`google-workspace-tab ${workspaceTab === 'gmail-chat' ? 'active' : ''}`}
        onClick={() => updateWorkspaceQuery('gmail-chat')}
        disabled={!GOOGLE_WORKSPACE_TAB_ENABLED['gmail-chat']}
        role="tab"
        aria-disabled={!GOOGLE_WORKSPACE_TAB_ENABLED['gmail-chat']}
        aria-selected={workspaceTab === 'gmail-chat'}
      >
        Gmail/Chat
      </button>
    </div>
  );

  const renderGoogleWorkspaceScaffold = (workspaceKey) => {
    const isCalendar = workspaceKey === 'calendar';
    const title = isCalendar ? 'Academic Calendar' : 'Gmail & Chat';
    const subtitle = isCalendar
      ? 'Manage session planning and sync with Google Workspace.'
      : 'Compose and send student follow-up messages with your connected Gmail account.';

    if (isCalendar) {
      const monthLabel = calendarMonthDate.toLocaleDateString([], { month: 'long', year: 'numeric' });
      const getUpcomingRelativeLabel = (date) => {
        if (!date) return 'Upcoming';
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000);
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Tomorrow';
        return target.toLocaleDateString([], { month: 'short', day: 'numeric' });
      };

      return (
        <div className="google-meet-page google-workspace-page google-calendar-page">
          <div className="page-header">
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
          {renderWorkspaceTabs()}

          {error && <div className="toast error" role="alert">{error}</div>}
          {successMessage && <div className="toast success" role="status">{successMessage}</div>}

          <section className="google-calendar-toolbar">
            <div className="google-calendar-connection">
              <div className={`google-calendar-connection-dot ${isConnected ? 'is-connected' : ''}`} />
              <div>
                <strong>{isConnected ? `Connected as ${googleEmail || 'Google account'}` : 'Google account not connected'}</strong>
                <span>{isConnected ? 'Calendar and event sync available' : 'Connect your Google account to load events'}</span>
              </div>
            </div>
            <div className="google-calendar-toolbar-actions">
              {!isConnected ? (
                <button className="btn btn-primary" onClick={handleConnect} disabled={loading}>
                  {loading ? 'Connecting...' : 'Connect Google'}
                </button>
              ) : (
                <>
                  <button className="btn btn-secondary" onClick={() => { fetchCalendarEvents(); fetchPlannedSessions(); }} disabled={calendarLoading || plannedSessionsLoading}>
                    {calendarLoading || plannedSessionsLoading ? 'Syncing...' : 'Sync Calendar'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => setCalendarViewMode((m) => m === 'compact' ? 'detailed' : 'compact')}>
                    {calendarViewMode === 'compact' ? 'Detailed View' : 'Compact View'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => {
                    setAssignSessionError(null);
                    setAssignSessionForm((prev) => ({ ...prev, join_url: prev.join_url || defaultSessionLink }));
                    setIsAssignSessionDrawerOpen(true);
                  }}>
                    + Assign Session
                  </button>
                  <button className="btn btn-primary" onClick={() => setIsCalendarDrawerOpen(true)}>
                    + Add New Event
                  </button>
                </>
              )}
            </div>
          </section>

          <section className="google-calendar-toolbar">
            <div className="google-calendar-connection">
              <div>
                <strong>Classroom / Meet launch link</strong>
                <span>{defaultSessionLink || 'No default link saved yet'}</span>
              </div>
            </div>
            <div className="google-calendar-toolbar-actions">
              {defaultSessionLink && (
                <button className="btn btn-secondary" onClick={() => window.open(defaultSessionLink, '_blank', 'noopener,noreferrer')}>
                  Launch
                </button>
              )}
              {isEditingSessionLink ? (
                <>
                  <input
                    className="google-workspace-input"
                    style={{ minWidth: '260px' }}
                    value={sessionLinkDraft}
                    onChange={(e) => setSessionLinkDraft(e.target.value)}
                    placeholder="https://meet.google.com/..."
                  />
                  <button className="btn btn-primary" onClick={handleSaveDefaultSessionLink} disabled={sessionLinkSaving}>
                    {sessionLinkSaving ? 'Saving...' : 'Save Link'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => { setSessionLinkDraft(defaultSessionLink); setIsEditingSessionLink(false); }}>
                    Cancel
                  </button>
                </>
              ) : (
                <button className="btn btn-secondary" onClick={() => setIsEditingSessionLink(true)}>
                  Edit Link
                </button>
              )}
            </div>
          </section>

          <section className="google-calendar-layout">
            <article className="google-calendar-main-card">
              <header className="google-calendar-main-header">
                <div className="google-calendar-month-nav">
                  <h2>{monthLabel}</h2>
                  <div className="google-calendar-month-buttons">
                    <button
                      type="button"
                      className="google-calendar-nav-btn"
                      aria-label="Previous month"
                      onClick={() => setCalendarMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="google-calendar-nav-btn"
                      aria-label="Next month"
                      onClick={() => setCalendarMonthDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                    >
                      ›
                    </button>
                  </div>
                </div>
              </header>

              <div className="google-calendar-weekdays">
                {CALENDAR_WEEKDAY_LABELS.map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>
              <div className="google-calendar-month-grid">
                {calendarMonthCells.map((cell) => {
                  const visibleEvents = cell.events.slice(0, 2);
                  const hiddenCount = Math.max(cell.events.length - visibleEvents.length, 0);
                  return (
                    <div
                      className={`google-calendar-cell ${cell.isCurrentMonth ? '' : 'is-outside-month'} ${cell.isToday ? 'is-today' : ''}`}
                      key={cell.date.toISOString()}
                    >
                      <div className="google-calendar-cell-date">{cell.date.getDate()}</div>
                      <div className="google-calendar-cell-events">
                        {visibleEvents.map((event) => (
                          <button
                            type="button"
                            key={event.id || `${event.summary}-${event.__startDate.toISOString()}`}
                            className={`google-calendar-chip ${event.__isDeadline ? 'is-deadline' : ''} ${event.__isPlannedSession ? 'is-planned' : ''}`}
                            onClick={() => event.__isPlannedSession ? openEditSession(event) : event.html_link && window.open(event.html_link, '_blank', 'noopener,noreferrer')}
                            title={event.summary || 'Event'}
                          >
                            <span>{formatClockTime(event.__startDate)}</span>
                            <strong>{event.__isPlannedSession ? event.title : (event.summary || 'Untitled event')}</strong>
                            {calendarViewMode === 'detailed' && event.__participantLabel && (
                              <em>{event.__participantLabel}</em>
                            )}
                          </button>
                        ))}
                        {hiddenCount > 0 && <span className="google-calendar-more">+{hiddenCount} more</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>

            <aside className="google-calendar-side">
              <article className="google-calendar-side-card">
                <div className="google-calendar-side-head">
                  <h3>Upcoming Sessions</h3>
                  <span>NEXT 48H</span>
                </div>
                <div className="google-calendar-side-list">
                  {upcomingSessions48h.length === 0 ? (
                    <p className="google-workspace-muted">No sessions in the next 48 hours.</p>
                  ) : (
                    upcomingSessions48h.map((event) => (
                      <button
                        type="button"
                        key={event.id || `${event.summary}-${event.__startDate.toISOString()}`}
                        className="google-calendar-side-item"
                        onClick={() => {
                          if (event.__isPlannedSession) {
                            openEditSession(event);
                          } else if (event.html_link) {
                            window.open(event.html_link, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        <div className="google-calendar-side-item-top">
                          <strong>{event.__isPlannedSession ? event.title : (event.summary || 'Untitled session')}</strong>
                          <span>{formatClockTime(event.__startDate)}</span>
                        </div>
                        <p>{getUpcomingRelativeLabel(event.__startDate)}, {formatDateLabel(event.__startDate)} ({formatDuration(event.__startDate, event.__endDate)})</p>
                        {event.__isPlannedSession && event.__participantLabel && (
                          <p>{event.__participantLabel}</p>
                        )}
                        {event.__isPlannedSession && event.join_url && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={(clickEvent) => {
                              clickEvent.preventDefault();
                              clickEvent.stopPropagation();
                              window.open(event.join_url, '_blank', 'noopener,noreferrer');
                            }}
                          >
                            Launch
                          </button>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </article>

              <article className="google-calendar-side-card is-deadlines">
                <div className="google-calendar-side-head">
                  <h3>Academic Deadlines</h3>
                </div>
                <div className="google-calendar-deadline-list">
                  {deadlineEvents.length === 0 ? (
                    <p className="google-workspace-muted">No deadline-tagged events yet.</p>
                  ) : (
                    deadlineEvents.map((event) => (
                      <div className="google-calendar-deadline-item" key={event.id || `${event.summary}-${event.__startDate.toISOString()}`}>
                        <i />
                        <div>
                          <strong>{event.summary || 'Untitled deadline'}</strong>
                          <span>{formatDueLabel(event.__startDate)}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </article>
            </aside>
          </section>

          {isCalendarDrawerOpen && (
            <div className="google-calendar-drawer-backdrop" role="presentation" onClick={() => setIsCalendarDrawerOpen(false)}>
              <aside className="google-calendar-drawer" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className="google-calendar-drawer-head">
                  <h3>Add New Event</h3>
                  <button type="button" className="google-calendar-drawer-close" onClick={() => setIsCalendarDrawerOpen(false)}>
                    ×
                  </button>
                </div>
                <div className="google-workspace-form-grid">
                  <label>
                    Event title
                    <input
                      className="google-workspace-input"
                      value={calendarForm.summary}
                      onChange={(e) => setCalendarForm((prev) => ({ ...prev, summary: e.target.value }))}
                      placeholder="Algebra tutoring session"
                    />
                  </label>
                  <label>
                    Start time
                    <input
                      type="datetime-local"
                      className="google-workspace-input"
                      value={calendarForm.start_time}
                      onChange={(e) => setCalendarForm((prev) => ({ ...prev, start_time: e.target.value }))}
                    />
                  </label>
                  <label>
                    End time
                    <input
                      type="datetime-local"
                      className="google-workspace-input"
                      value={calendarForm.end_time}
                      onChange={(e) => setCalendarForm((prev) => ({ ...prev, end_time: e.target.value }))}
                    />
                  </label>
                  <label>
                    Attendees (comma-separated)
                    <input
                      className="google-workspace-input"
                      value={calendarForm.attendees}
                      onChange={(e) => setCalendarForm((prev) => ({ ...prev, attendees: e.target.value }))}
                      placeholder="student@example.com,parent@example.com"
                    />
                  </label>
                  <label className="google-workspace-form-span">
                    Description
                    <textarea
                      className="google-workspace-input google-workspace-textarea"
                      value={calendarForm.description}
                      onChange={(e) => setCalendarForm((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Goals, prep notes, or assignment reminders"
                    />
                  </label>
                </div>
                <div className="google-calendar-drawer-actions">
                  <button className="btn btn-secondary" onClick={() => setIsCalendarDrawerOpen(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleCreateCalendarEvent} disabled={loading || !isConnected}>
                    {loading ? 'Creating...' : 'Create Event'}
                  </button>
                </div>
              </aside>
            </div>
          )}

          {isAssignSessionDrawerOpen && (
            <div
              className="google-calendar-drawer-backdrop"
              role="presentation"
              onClick={() => setIsAssignSessionDrawerOpen(false)}
            >
              <aside
                className="google-calendar-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Assign session"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="google-calendar-drawer-head">
                  <h3>Assign Session</h3>
                  <button
                    type="button"
                    className="google-calendar-drawer-close"
                    onClick={() => setIsAssignSessionDrawerOpen(false)}
                  >
                    ×
                  </button>
                </div>

                {assignSessionError && (
                  <div style={{ margin: '0 0 0.75rem', padding: '0.6rem 0.75rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', fontSize: '0.85rem' }}>
                    {assignSessionError}
                  </div>
                )}

                <div className="google-workspace-form-grid">
                  <label className="google-workspace-form-span">
                    Student
                    <select
                      className="google-workspace-input"
                      value={assignSessionForm.student_uuid}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, student_uuid: e.target.value }))
                      }
                    >
                      <option value="">— Select a student —</option>
                      {scopedStudents.map((s) => (
                        <option key={s.uuid} value={s.uuid}>
                          {s.display_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.uuid}
                          {s.owner ? ` (${s.owner})` : ''}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="google-workspace-form-span">
                    Session title
                    <input
                      className="google-workspace-input"
                      value={assignSessionForm.title}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, title: e.target.value }))
                      }
                      placeholder="Algebra review"
                    />
                  </label>

                  <label>
                    Date
                    <input
                      type="date"
                      className="google-workspace-input"
                      value={assignSessionForm.session_date}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, session_date: e.target.value }))
                      }
                    />
                  </label>

                  <label>
                    Start time
                    <select
                      className="google-workspace-input"
                      value={assignSessionForm.session_time}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, session_time: e.target.value }))
                      }
                    >
                      {Array.from({ length: 48 }, (_, i) => {
                        const totalHour = Math.floor(i / 2);
                        const m = i % 2 === 0 ? '00' : '30';
                        const val = `${totalHour.toString().padStart(2, '0')}:${m}`;
                        const ampm = totalHour < 12 ? 'AM' : 'PM';
                        const displayHour = totalHour % 12 === 0 ? 12 : totalHour % 12;
                        const label = `${displayHour}:${m} ${ampm}`;
                        return <option key={val} value={val}>{label}</option>;
                      })}
                    </select>
                  </label>

                  <label>
                    Duration (minutes)
                    <input
                      type="number"
                      className="google-workspace-input"
                      min="15"
                      max="480"
                      step="15"
                      value={assignSessionForm.duration_minutes}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({
                          ...prev,
                          duration_minutes: parseInt(e.target.value, 10) || 60,
                        }))
                      }
                    />
                  </label>

                  <label className="google-workspace-form-span">
                    Participant emails
                    <input
                      className="google-workspace-input"
                      value={assignSessionForm.participant_emails}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, participant_emails: e.target.value }))
                      }
                      placeholder="student@example.com,parent@example.com"
                    />
                  </label>

                  <label className="google-workspace-form-span">
                    Participant names
                    <input
                      className="google-workspace-input"
                      value={assignSessionForm.participant_names}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, participant_names: e.target.value }))
                      }
                      placeholder="Student, Parent"
                    />
                  </label>

                  <label className="google-workspace-form-span">
                    Classroom / Meet link
                    <input
                      className="google-workspace-input"
                      value={assignSessionForm.join_url}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, join_url: e.target.value }))
                      }
                      placeholder="https://meet.google.com/..."
                    />
                  </label>

                  <label className="google-workspace-form-span">
                    Location
                    <input
                      className="google-workspace-input"
                      value={assignSessionForm.location}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, location: e.target.value }))
                      }
                      placeholder="Google Meet, classroom, or room"
                    />
                  </label>

                  <label className="google-workspace-form-span">
                    Description
                    <textarea
                      className="google-workspace-input google-workspace-textarea"
                      value={assignSessionForm.description}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({ ...prev, description: e.target.value }))
                      }
                      placeholder="Goals, topics, or prep notes for this session"
                    />
                  </label>

                  {isConnected && (
                    <label
                      className="google-workspace-form-span"
                      style={{ flexDirection: 'row', gap: '0.5rem', alignItems: 'center' }}
                    >
                      <input
                        type="checkbox"
                        checked={assignSessionForm.also_add_to_google_calendar}
                        onChange={(e) =>
                          setAssignSessionForm((prev) => ({
                            ...prev,
                            also_add_to_google_calendar: e.target.checked,
                          }))
                        }
                      />
                      Also add to Google Calendar
                    </label>
                  )}

                  <label
                    className="google-workspace-form-span"
                    style={{ flexDirection: 'row', gap: '0.5rem', alignItems: 'center' }}
                  >
                    <input
                      type="checkbox"
                      checked={assignSessionForm.notify_student}
                      onChange={(e) =>
                        setAssignSessionForm((prev) => ({
                          ...prev,
                          notify_student: e.target.checked,
                        }))
                      }
                    />
                    Notify student in app
                  </label>
                </div>

                <div className="google-calendar-drawer-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsAssignSessionDrawerOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleAssignSession}
                    disabled={
                      assignSessionLoading ||
                      !assignSessionForm.student_uuid ||
                      !assignSessionForm.title ||
                      !assignSessionForm.session_date
                    }
                  >
                    {assignSessionLoading ? 'Assigning...' : 'Assign Session'}
                  </button>
                </div>
              </aside>
            </div>
          )}

          {editingSession && sessionEditForm && (
            <div
              className="google-calendar-drawer-backdrop"
              role="presentation"
              onClick={() => { setEditingSession(null); setSessionEditForm(null); }}
            >
              <aside
                className="google-calendar-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Edit session"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="google-calendar-drawer-head">
                  <h3>Edit Session</h3>
                  <button
                    type="button"
                    className="google-calendar-drawer-close"
                    onClick={() => { setEditingSession(null); setSessionEditForm(null); }}
                  >
                    ×
                  </button>
                </div>

                <div className="google-workspace-form-grid">
                  <label className="google-workspace-form-span">
                    Session title
                    <input
                      className="google-workspace-input"
                      value={sessionEditForm.title}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, title: e.target.value }))}
                    />
                  </label>
                  <label>
                    Start time
                    <input
                      type="datetime-local"
                      className="google-workspace-input"
                      value={sessionEditForm.scheduled_at}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, scheduled_at: e.target.value }))}
                    />
                  </label>
                  <label>
                    Duration
                    <input
                      type="number"
                      min="15"
                      max="480"
                      step="15"
                      className="google-workspace-input"
                      value={sessionEditForm.duration_minutes}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, duration_minutes: parseInt(e.target.value, 10) || 60 }))}
                    />
                  </label>
                  <label>
                    Status
                    <select
                      className="google-workspace-input"
                      value={sessionEditForm.status}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, status: e.target.value }))}
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </label>
                  <label className="google-workspace-form-span">
                    Participant emails
                    <input
                      className="google-workspace-input"
                      value={sessionEditForm.participant_emails}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, participant_emails: e.target.value }))}
                    />
                  </label>
                  <label className="google-workspace-form-span">
                    Participant names
                    <input
                      className="google-workspace-input"
                      value={sessionEditForm.participant_names}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, participant_names: e.target.value }))}
                    />
                  </label>
                  <label className="google-workspace-form-span">
                    Classroom / Meet link
                    <input
                      className="google-workspace-input"
                      value={sessionEditForm.join_url}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, join_url: e.target.value }))}
                    />
                  </label>
                  <label className="google-workspace-form-span">
                    Location
                    <input
                      className="google-workspace-input"
                      value={sessionEditForm.location}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, location: e.target.value }))}
                    />
                  </label>
                  <label className="google-workspace-form-span">
                    Description
                    <textarea
                      className="google-workspace-input google-workspace-textarea"
                      value={sessionEditForm.description}
                      onChange={(e) => setSessionEditForm((prev) => ({ ...prev, description: e.target.value }))}
                    />
                  </label>
                </div>

                <div className="google-calendar-drawer-actions">
                  {sessionEditForm.join_url && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => window.open(sessionEditForm.join_url, '_blank', 'noopener,noreferrer')}
                    >
                      Launch
                    </button>
                  )}
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setEditingSession(null); setSessionEditForm(null); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleUpdatePlannedSession}
                    disabled={assignSessionLoading || !sessionEditForm.title || !sessionEditForm.scheduled_at}
                  >
                    {assignSessionLoading ? 'Saving...' : 'Save Session'}
                  </button>
                </div>
              </aside>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="google-meet-page google-workspace-page">
        <div className="page-header">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        {renderWorkspaceTabs()}

        {error && <div className="toast error" role="alert">{error}</div>}
        {successMessage && <div className="toast success" role="status">{successMessage}</div>}

        <section className="google-workspace-account-card">
          <h3>Google Account Connection</h3>
          <p>
            {isConnected
              ? `Connected as ${googleEmail || 'Google account'}`
              : 'No Google account connected'}
          </p>
          <div className="google-workspace-account-actions">
            {!isConnected ? (
              <button className="btn btn-primary" onClick={handleConnect} disabled={loading}>
                {loading ? 'Connecting...' : 'Connect Google'}
              </button>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={() => checkAuthStatus(googleEmail)} disabled={loading || !googleEmail}>
                  Refresh Status
                </button>
                <button className="btn btn-ghost btn-small" onClick={handleDisconnect} disabled={loading}>
                  Disconnect
                </button>
              </>
            )}
          </div>
        </section>

        <section className="gmail-hub-layout">
          {gmailScopeReconnectNeeded && (
            <div className="gmail-reconnect-banner" role="alert">
              <div>
                <strong>Gmail permissions need to be updated.</strong>
                <p>Reconnect Google and grant Gmail inbox access to load messages and threads.</p>
              </div>
              <button className="btn btn-primary btn-small" onClick={handleConnect} disabled={loading}>
                {loading ? 'Connecting...' : 'Reconnect Google'}
              </button>
            </div>
          )}
          <aside className="gmail-inbox-panel">
            <div className="gmail-inbox-search">
              <input
                className="google-workspace-input"
                value={gmailSearchInput}
                onChange={(e) => setGmailSearchInput(e.target.value)}
                placeholder="Search messages..."
              />
            </div>
            <div className="gmail-inbox-title-row">
              <h3>Recent Messages</h3>
            </div>
            <div className="gmail-inbox-list">
              {gmailInboxLoading ? (
                <p className="google-workspace-muted">Loading inbox...</p>
              ) : gmailInbox.length === 0 ? (
                <p className="google-workspace-muted">No messages found for this query.</p>
              ) : (
                gmailInbox.map((item) => {
                  const isActive = item.thread_id === gmailSelectedThreadId;
                  return (
                    <button
                      type="button"
                      key={item.id || item.thread_id}
                      className={`gmail-inbox-item ${isActive ? 'is-active' : ''}`}
                      onClick={() => setGmailSelectedThreadId(item.thread_id || '')}
                    >
                      <div className="gmail-inbox-item-head">
                        <strong>{item.sender_name || item.sender_email || GMAIL_NAME_FALLBACK}</strong>
                        <span>{formatThreadTimestamp(item.internal_date || item.date)}</span>
                      </div>
                      <p className="gmail-inbox-item-subject">{item.subject || '(No Subject)'}</p>
                      <p className="gmail-inbox-item-snippet">{item.snippet || ''}</p>
                    </button>
                  );
                })
              )}
            </div>
          </aside>

          <section className="gmail-thread-panel">
            <header className="gmail-thread-header">
              <div>
                <h2>{gmailSelectedThread?.messages?.[0]?.from_name || gmailSelectedThread?.messages?.[0]?.from_email || 'Select a message'}</h2>
                <p>{gmailSelectedThread ? 'Conversation thread' : 'Pick an inbox item to view full thread'}</p>
              </div>
            </header>

            <div className="gmail-thread-scroll">
              {gmailThreadLoading ? (
                <p className="google-workspace-muted">Loading conversation...</p>
              ) : !gmailSelectedThread || !(gmailSelectedThread.messages || []).length ? (
                <p className="google-workspace-muted">No thread selected yet.</p>
              ) : (
                (gmailSelectedThread.messages || []).map((message) => (
                  <article className={`gmail-thread-message ${message.is_from_me ? 'is-sent' : 'is-received'}`} key={message.id}>
                    <div className="gmail-thread-bubble">
                      {!message.is_from_me && message.subject && (
                        <h4>Subject: {message.subject}</h4>
                      )}
                      <p>{message.body_text || message.snippet || ''}</p>
                    </div>
                    <time>{formatThreadTimestamp(message.internal_date || message.date)}</time>
                  </article>
                ))
              )}
            </div>

            <div className="gmail-composer">
              <div className="gmail-compose-meta">
                <input
                  className="google-workspace-input"
                  value={gmailForm.to}
                  onChange={(e) => setGmailForm((prev) => ({ ...prev, to: e.target.value }))}
                  placeholder="To"
                />
                <input
                  className="google-workspace-input"
                  value={gmailForm.subject}
                  onChange={(e) => setGmailForm((prev) => ({ ...prev, subject: e.target.value }))}
                  placeholder="Subject"
                />
              </div>
              <textarea
                className="google-workspace-input google-workspace-textarea"
                value={gmailForm.body_text}
                onChange={(e) => setGmailForm((prev) => ({ ...prev, body_text: e.target.value }))}
                placeholder="Type a message or use '/' for coaching templates..."
              />
              <div className="gmail-composer-actions">
                <button className="btn btn-secondary" onClick={() => handleGmailAction('draft')} disabled={gmailSending || !isConnected}>
                  {gmailSending ? 'Working...' : 'Create Draft'}
                </button>
                <button className="btn btn-primary" onClick={() => handleGmailAction('send')} disabled={gmailSending || !isConnected}>
                  {gmailSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </section>
        </section>
      </div>
    );
  };

  const renderAnalyticsLayout = () => {
    const parsedSummary = normalizeSummary(selectedAnalyticsTranscript?.summary);
    const listOrEmpty = (items, fallback) => {
      if (!Array.isArray(items) || items.length === 0) return <p className="analytics-empty-text">{fallback}</p>;
      return (
        <ul className="analytics-list">
          {items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
        </ul>
      );
    };

    return (
      <>
        <div className="master-detail-toolbar">
          <button
            className="btn btn-secondary btn-small"
            onClick={() => setIsListCollapsed((prev) => !prev)}
          >
            {isListCollapsed ? 'Show List' : 'Hide List'}
          </button>
        </div>
        <div className={`master-detail-layout ${isListCollapsed ? 'is-list-collapsed' : ''}`}>
        <div className="transcript-list-panel">
          {loading && analyticsTranscripts.length === 0 ? (
            <div className="loading-state">Loading analyzed transcripts...</div>
          ) : analyticsTranscripts.length === 0 ? (
            <div className="empty-list-state">
              <p>No AI-analyzed transcripts yet</p>
              <p className="hint">Create or upload a transcript, then run AI Summarize.</p>
            </div>
          ) : (
            <div className="transcript-list">
              {analyticsTranscripts.map((transcript) => (
                <TranscriptCard
                  key={transcript.id}
                  transcript={transcript}
                  isSelected={selectedAnalyticsTranscript?.id === transcript.id}
                  onClick={handleSelectAnalyticsTranscript}
                  onRegenerateSummary={() => runAiSummary(transcript.id)}
                  regenerating={isAnalyzing && activeAnalysisTranscriptId === transcript.id}
                  formatDate={formatDate}
                  formatDuration={formatDuration}
                />
              ))}
            </div>
          )}
        </div>
        <div className="transcript-detail-wrapper">
          {!selectedAnalyticsTranscript ? (
            <div className="analytics-detail-empty">
              <h3>Select an analyzed transcript</h3>
              <p>Choose a transcript on the left to review AI analytics.</p>
            </div>
          ) : analyticsLoading ? (
            <div className="analytics-detail-empty">
              <h3>Loading analysis...</h3>
            </div>
          ) : (
            <div className="analytics-detail-panel">
              <div className="analytics-detail-header">
                <h2>{selectedAnalyticsTranscript.meeting_title || 'Transcript Analysis'}</h2>
                <div className="analytics-detail-meta">
                  <span>{formatDate(selectedAnalyticsTranscript.start_time || selectedAnalyticsTranscript.created_at)}</span>
                  <span className="meta-separator">|</span>
                  <span>Source: AI analysis</span>
                  {selectedAnalyticsTranscript.analysis_status && (
                    <>
                      <span className="meta-separator">|</span>
                      <span>Status: {selectedAnalyticsTranscript.analysis_status}</span>
                    </>
                  )}
                </div>
                <div className="analytics-detail-actions">
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={() => runAiSummary(selectedAnalyticsTranscript.id)}
                    disabled={isAnalyzing && activeAnalysisTranscriptId === selectedAnalyticsTranscript.id}
                  >
                    {isAnalyzing && activeAnalysisTranscriptId === selectedAnalyticsTranscript.id ? 'Reanalyzing...' : 'Reanalyze'}
                  </button>
                  <button className="btn btn-primary btn-small" onClick={openFullTranscriptFromAnalytics}>
                    Open Full Transcript
                  </button>
                </div>
              </div>
              <div className="analytics-detail-content">
                {parsedSummary?.session_overview && (
                  <section className="analytics-section">
                    <h4>Session Overview</h4>
                    <p>{parsedSummary.session_overview}</p>
                  </section>
                )}
                <section className="analytics-section">
                  <h4>Topics Covered</h4>
                  {listOrEmpty(parsedSummary?.topics_covered, 'No topics identified.')}
                </section>
                <section className="analytics-section">
                  <h4>Learning Outcomes</h4>
                  {listOrEmpty(parsedSummary?.learning_outcomes, 'No learning outcomes identified.')}
                </section>
                <section className="analytics-section">
                  <h4>Challenges Identified</h4>
                  {listOrEmpty(parsedSummary?.challenges_identified, 'No challenges identified.')}
                </section>
                <section className="analytics-section">
                  <h4>Next Steps</h4>
                  {listOrEmpty(parsedSummary?.next_steps, 'No next steps available.')}
                </section>
              </div>
            </div>
          )}
        </div>
        </div>
      </>
    );
  };

  const currentRetryingSectionKey = retryingSection && selectedTranscript
    && retryingSection.transcriptId === selectedTranscript.id
    ? retryingSection.sectionKey
    : null;

  if (!studentMode && (workspaceTab === 'calendar' || workspaceTab === 'gmail-chat')) {
    return renderGoogleWorkspaceScaffold(workspaceTab);
  }

  if (isRestoringAuth) {
    return (
      <div className="google-meet-page">
        <div className="page-header">
          <h1>Google Meet Transcripts</h1>
          <p>Restoring Google connection...</p>
        </div>
        {renderWorkspaceTabs()}
      </div>
    );
  }

  // Render not connected state (but allow Manual Entry tab)
  if (!isConnected && activeTab !== 'manual' && activeTab !== 'uploaded' && activeTab !== 'analytics') {
    return (
      <div className="google-meet-page">
        <div className="page-header">
          <h1>Google Meet Transcripts</h1>
          <p>Connect your Google Workspace account to sync meeting transcripts</p>
        </div>
        {renderWorkspaceTabs()}

        {error && (
          <div className="toast error">{error}</div>
        )}
        {successMessage && (
          <div className="toast success">{successMessage}</div>
        )}

        {renderMainTabs('disconnected')}

        <div className="connect-card">
          <div className="connect-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M15 10l-4 4l6 6l4-16l-18 7l6 2l8-8" />
            </svg>
          </div>
          <h2>Connect Google Account</h2>
          <p>
            Link your Google Workspace account to automatically sync your
            Google Meet transcripts for session review.
          </p>
          <button
            className="btn btn-primary btn-large"
            onClick={handleConnect}
            disabled={loading}
          >
            {loading ? 'Connecting...' : 'Connect with Google'}
          </button>
          {!studentMode && (
            <p style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: 'var(--lrbrain-text-light)' }}>
              Or use <strong>Manual Entry</strong> to add transcripts without Google integration.
            </p>
          )}
        </div>
      </div>
    );
  }

  // Render Manual Entry tab when not connected
  if (!isConnected && activeTab === 'manual') {
    return (
      <div className="google-meet-page">
        <div className="page-header">
          <h1>Session Transcripts</h1>
          <p>Manage session transcripts for analysis and planning</p>
        </div>
        {renderWorkspaceTabs()}

        {error && <div className="toast error" role="alert">{error}</div>}
        {successMessage && <div className="toast success" role="status">{successMessage}</div>}

        {renderMainTabs('disconnected')}

        <ManualTranscriptForm
          students={scopedStudents}
          onTranscriptCreated={handleManualTranscriptCreated}
          onViewUploaded={(transcript) => jumpToUploadedTranscript(transcript, false)}
          onSummarizeAndView={(transcript) => jumpToUploadedTranscript(transcript, true)}
        />
      </div>
    );
  }

  // Render Uploaded Transcripts tab when not connected
  if (!isConnected && activeTab === 'uploaded') {
    return (
      <div className="google-meet-page">
        <div className="page-header">
          <h1>Uploaded Transcripts</h1>
          <p>View and summarize your manual transcript uploads</p>
        </div>
        {renderWorkspaceTabs()}

        {error && <div className="toast error" role="alert">{error}</div>}
        {successMessage && <div className="toast success" role="status">{successMessage}</div>}
        {isAnalyzing && (
          <div className="toast success" role="status">
            AI Analysis in progress: {analysisProgress?.message || 'Analyzing transcript...'}
          </div>
        )}

        {renderMainTabs('disconnected')}

        <div className="master-detail-toolbar">
          <button
            className="btn btn-secondary btn-small"
            onClick={() => setIsListCollapsed((prev) => !prev)}
          >
            {isListCollapsed ? 'Show List' : 'Hide List'}
          </button>
        </div>

        <div className={`master-detail-layout ${isListCollapsed ? 'is-list-collapsed' : ''}`}>
          <div className="transcript-list-panel">
            {loading && transcripts.length === 0 ? (
              <div className="loading-state">Loading transcripts...</div>
            ) : transcripts.length === 0 ? (
              <div className="empty-list-state">
                <p>No uploaded transcripts yet</p>
                <p className="hint">Create one in Manual Entry to see it here</p>
              </div>
            ) : (
              <div className="transcript-list">
                {transcripts.map((transcript) => (
                  <TranscriptCard
                    key={transcript.id}
                    transcript={transcript}
                    isSelected={selectedTranscript?.id === transcript.id}
                    onClick={handleSelectTranscript}
                    onRegenerateSummary={() => runAiSummary(transcript.id)}
                    regenerating={isAnalyzing && activeAnalysisTranscriptId === transcript.id}
                    formatDate={formatDate}
                    formatDuration={formatDuration}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="transcript-detail-wrapper">
            <TranscriptDetailPanel
              transcript={selectedTranscript}
              students={scopedStudents}
              onStudentLinked={handleStudentLinked}
              onSummaryUpdated={handleSummaryUpdated}
              onAiSummarize={runAiSummary}
              onRetrySection={retryAiSummarySection}
              retryingSectionKey={currentRetryingSectionKey}
              isAnalyzingTranscript={isAnalyzing && activeAnalysisTranscriptId === selectedTranscript?.id}
              formatDate={formatDate}
              formatDuration={formatDuration}
            />
          </div>
        </div>
      </div>
    );
  }

  // Render Analytics tab when not connected
  if (!isConnected && activeTab === 'analytics') {
    return (
      <div className="google-meet-page">
        <div className="page-header">
          <h1>Transcript Analytics</h1>
          <p>Review AI-analyzed transcript insights</p>
        </div>
        {renderWorkspaceTabs()}

        {error && <div className="toast error" role="alert">{error}</div>}
        {successMessage && <div className="toast success" role="status">{successMessage}</div>}
        {isAnalyzing && (
          <div className="toast success" role="status">
            AI Analysis in progress: {analysisProgress?.message || 'Analyzing transcript...'}
          </div>
        )}

        {renderMainTabs('disconnected')}
        {renderAnalyticsLayout()}
      </div>
    );
  }

  return (
    <div className="google-meet-page google-meet-page--stitch">
      <div className="page-header page-header--stitch">
        <div className="header-content header-content--stitch">
          <div className="header-title-group">
            <h1>Google Meet Transcripts</h1>
            <p>Review and manage your academic session recordings</p>
          </div>
          <div className="header-actions header-actions--stitch">
            <div className="gm-connection-pill" role="status" aria-live="polite">
              <span className="gm-connection-dot" />
              CONNECTED: {googleEmail || 'Google account'}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => handleSync('manual')}
              disabled={syncing || !isConnected}
            >
              {syncing ? (
                <>
                  <span className="btn-spinner"></span>
                  Syncing...
                </>
              ) : (
                'Sync Now'
              )}
            </button>
            <button
              className="btn btn-ghost btn-small"
              onClick={handleDisconnect}
              disabled={loading}
            >
              Disconnect
            </button>
          </div>
        </div>
      </div>
      {renderWorkspaceTabs()}
      <div className="google-meet-content-shell">
        <section className="gm-summary-zone">
          <div className="gm-stats-grid">
            {summaryStats.map((stat) => (
              <article
                key={stat.key}
                className={`gm-stat-card ${stat.tone === 'coral' ? 'gm-stat-card--alert' : 'gm-stat-card--primary'}`}
              >
                <div>
                  <p className="gm-stat-label">{stat.label}</p>
                  <p className="gm-stat-value">{stat.value}</p>
                </div>
                <div className={`gm-stat-icon gm-stat-icon--${stat.tone}`}>
                  {renderStatIcon(stat.icon)}
                </div>
              </article>
            ))}
          </div>
          <div className="gm-sync-meta">
            <span>Last synced: {formatRelativeTime(lastSyncAt)}</span>
            {autoSyncStatus && (
              <span className={`gm-auto-sync-status gm-auto-sync-status--${autoSyncStatus.type}`}>
                {autoSyncStatus.message}
              </span>
            )}
          </div>
        </section>

        {/* Toast Messages */}
        {error && <div className="toast error" role="alert">{error}</div>}
        {successMessage && <div className="toast success" role="status">{successMessage}</div>}
        {isAnalyzing && (
          <div className="toast success" role="status">
            AI Analysis in progress: {analysisProgress?.message || 'Analyzing transcript...'}
          </div>
        )}

        {/* Tab Navigation */}
        {renderMainTabs('connected')}

        {activeTab === 'synced' ? (
          <>
            <SyncStatusPanel
              isConnected={isConnected}
              googleEmail={googleEmail}
              lastSyncAt={lastSyncAt}
              syncResults={syncResults}
              syncing={syncing}
              error={null}
              onSync={() => handleSync('manual')}
              counts={counts}
              searchScope={searchScope}
              permissions={permissions}
              warnings={visibleWarnings}
              onDismissWarnings={handleDismissWarnings}
              hideSyncButton={true}
              hideStats={true}
              className="sync-status-panel--details"
            />

            {/* Master-Detail Layout */}
            <div className="master-detail-toolbar">
              <button
                className="btn btn-secondary btn-small"
                onClick={() => setIsListCollapsed((prev) => !prev)}
              >
                {isListCollapsed ? 'Show List' : 'Hide List'}
              </button>
            </div>

            <div className={`master-detail-layout ${isListCollapsed ? 'is-list-collapsed' : ''}`}>
              {/* Left Panel: Transcript List */}
              <div className="transcript-list-panel">
                {loading && transcripts.length === 0 ? (
                  <div className="loading-state">Loading transcripts...</div>
                ) : transcripts.length === 0 ? (
                  <div className="empty-list-state">
                    <p>No transcripts synced yet</p>
                    <p className="hint">Click "Sync Now" to get started</p>
                  </div>
                ) : (
                  <div className="transcript-list">
                    {transcripts.map((transcript) => (
                      <TranscriptCard
                        key={transcript.id}
                        transcript={transcript}
                        isSelected={selectedTranscript?.id === transcript.id}
                        onClick={handleSelectTranscript}
                        onRegenerateSummary={handleRegenerateSummary}
                        regenerating={regeneratingSummaryId === transcript.id}
                        formatDate={formatDate}
                        formatDuration={formatDuration}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Right Panel: Detail View */}
              <div className="transcript-detail-wrapper">
                <TranscriptDetailPanel
                  transcript={selectedTranscript}
                  students={scopedStudents}
                  onStudentLinked={handleStudentLinked}
                  onSummaryUpdated={handleSummaryUpdated}
                  onAiSummarize={runAiSummary}
                  onRetrySection={retryAiSummarySection}
                  retryingSectionKey={currentRetryingSectionKey}
                  isAnalyzingTranscript={isAnalyzing && activeAnalysisTranscriptId === selectedTranscript?.id}
                  formatDate={formatDate}
                  formatDuration={formatDuration}
                />
              </div>
            </div>
          </>
        ) : activeTab === 'browse' ? (
          <BrowseNotesTab
            googleEmail={googleEmail}
            formatDate={formatDate}
          />
        ) : activeTab === 'analytics' ? (
          renderAnalyticsLayout()
        ) : (
          <ManualTranscriptForm
            students={scopedStudents}
            onTranscriptCreated={handleManualTranscriptCreated}
            onViewUploaded={(transcript) => jumpToUploadedTranscript(transcript, false)}
            onSummarizeAndView={(transcript) => jumpToUploadedTranscript(transcript, true)}
          />
        )}
      </div>
    </div>
  );
};

export default GoogleMeetSync;
